import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { IAccount, IAccountingOverview } from '../../interfaces/accounting.interface';
import MoneyDialog, { MoneyMode } from './MoneyDialog';
import { formatMoney } from './money';
import { useAccountingInstitution } from './useAccountingInstitution';

const Stat: React.FC<{ label: string; value: number; tone?: 'success' | 'error' }> = ({ label, value, tone }) => (
    <Card variant="outlined">
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h6" color={tone ? `${tone}.main` : undefined}>{formatMoney(value)}</Typography>
        </CardContent>
    </Card>
);

const AccountingOverview: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showError, showApiError } = useToast();
    const [overview, setOverview] = useState<IAccountingOverview | null>(null);
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [dialog, setDialog] = useState<MoneyMode | null>(null);

    const load = useCallback(async () => {
        if (!institutionId) return;
        try {
            const [o, a] = await Promise.all([
                AccountingService.overview(institutionId),
                AccountingService.listAccounts(institutionId),
            ]);
            setOverview(o);
            setAccounts(a);
        } catch (error) {
            showApiError(error, t('overview.loadFailed'));
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const retry = async (kind: string, id: string) => {
        try {
            const result = await AccountingService.retryPosting(institutionId, kind, id);
            if (result.posted) showSuccess(t('overview.posted'));
            else showError(t('overview.stillNotPosted', { reason: result.error ?? t('overview.unknownReason') }));
        } catch (error) {
            showApiError(error, t('overview.retryFailed'));
        }
        load();
    };

    if (!overview) return <Box sx={{ p: 3 }}><Typography>{t('common:status.loading')}</Typography></Box>;
    const net = overview.monthIncome - overview.monthExpense;

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5">{t('overview.title')}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="contained" onClick={() => setDialog('in')}>{t('moneyDialog.title.in')}</Button>
                    <Button variant="contained" color="secondary" onClick={() => setDialog('out')}>{t('moneyDialog.title.out')}</Button>
                    <Button variant="outlined" onClick={() => setDialog('transfer')}>{t('moneyDialog.title.transfer')}</Button>
                    <Button component={RouterLink} to="/accounting/entries/new">{t('overview.journalEntry')}</Button>
                </Stack>
            </Stack>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('overview.cashAndBank')}</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {overview.cashAndBank.map(a => (
                    <Grid key={a.id} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Stat label={`${a.code} · ${a.name}`} value={a.balance} />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('overview.thisMonth')}</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 4 }}><Stat label={t('figures.income')} value={overview.monthIncome} /></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><Stat label={t('figures.expenses')} value={overview.monthExpense} /></Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <Stat label={net >= 0 ? t('figures.profit') : t('figures.loss')} value={net} tone={net >= 0 ? 'success' : 'error'} />
                </Grid>
            </Grid>

            {overview.failedPostings.length > 0 && (
                <Paper sx={{ p: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('overview.failedPostingsIntro')}
                    </Alert>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow><TableCell>{t('overview.item')}</TableCell><TableCell>{t('overview.error')}</TableCell><TableCell /></TableRow>
                            </TableHead>
                            <TableBody>
                                {overview.failedPostings.map(f => (
                                    <TableRow key={`${f.kind}-${f.id}`}>
                                        <TableCell>{f.label}</TableCell>
                                        <TableCell>{f.error}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => retry(f.kind, f.id)}>{t('common:actions.retry')}</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>
            )}

            <MoneyDialog
                open={dialog !== null}
                mode={dialog ?? 'in'}
                institutionId={institutionId}
                accounts={accounts}
                onClose={() => setDialog(null)}
                onSaved={() => { setDialog(null); load(); }}
            />
        </Box>
    );
};

export default AccountingOverview;
